import { FC } from 'react';
import { useSelector } from 'react-redux';
import { graphql, useFragment } from 'react-relay';
import { useAuctionBids } from 'dibs-auctions/exports/hooks';

// components
import { PriceDisplay } from 'dibs-buyer-price';
import { FormattedMessage } from 'dibs-react-intl';
import { PdpSharedAuctionActionBidLabel } from './PdpSharedAuctionActionBidLabel';
import { PdpSharedAuctionBidButton } from '../PdpSharedAuctionBidButton/PdpSharedAuctionBidButton';
import { PdpSharedAuctionBidsDetails } from '../PdpSharedAuctionBidsDetails/PdpSharedAuctionBidsDetails';
import { PdpSharedAuctionCountdownLazy } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdownLazy';

// types
import { PdpSharedAuctionActionBid_item$key as Item } from './__generated__/PdpSharedAuctionActionBid_item.graphql';

// styles
import styles from './PdpSharedAuctionActionBid.scss';

type Props = {
    item: Item;
    currency: string;
    isBiddingDisabled?: boolean;
    onAuctionBidClick: () => void;
    onCountdownFinished?: () => void;
    isCloseToReserve: boolean;
};

type RelayVariables = {
    userId: string;
};

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

const increaseBidLabel = (
    <FormattedMessage id="PdpSharedAuctionActionBid.increaseBid" defaultMessage="Increase Bid" />
);

const itemFragment = graphql`
    fragment PdpSharedAuctionActionBid_item on Item {
        ...PriceDisplay_item @arguments(userId: $userId, isTrade: $isTrade)
        ...useAuctionBids_item @arguments(fetchUser: $fetchUser)
        ...PdpSharedAuctionBidsDetails_item
        ...PdpSharedAuctionCountdownLazy_item
        ...PdpSharedAuctionActionBidLabel_item
    }
`;

export const PdpSharedAuctionActionBid: FC<Props> = ({
    item: itemRef,
    currency,
    onAuctionBidClick,
    isBiddingDisabled,
    onCountdownFinished,
    isCloseToReserve,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { userId } = useSelector<ReduxStoreType, RelayVariables>(
        relay => relay?.relayVariables?.variables || {}
    );
    const { hasBid } = useAuctionBids({ item, userId, currency });

    return (
        <>
            <PdpSharedAuctionCountdownLazy item={item} onCountdownFinished={onCountdownFinished} />
            <div className={styles.container}>
                <span className={styles.bid}>
                    <FormattedMessage
                        id="PdpSharedAuctionActionBid.currentBid"
                        defaultMessage="Current Bid"
                    />
                </span>
                <div className={styles.priceInfo}>
                    <PriceDisplay currency={currency} item={item} useSvgIcon sku={null} />
                    <PdpSharedAuctionBidsDetails
                        item={item}
                        currency={currency}
                        className={styles.bidDetails}
                    />
                </div>
                <PdpSharedAuctionActionBidLabel
                    item={item}
                    currency={currency}
                    isCloseToReserve={isCloseToReserve}
                />
                <PdpSharedAuctionBidButton
                    onClick={onAuctionBidClick}
                    disabled={isBiddingDisabled}
                    label={hasBid ? increaseBidLabel : null}
                />
            </div>
        </>
    );
};
